import { createRef, useContext } from 'react';
import { observer } from 'mobx-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import i18nContext from 'components/i18n-context';
import { getErrorMessageForAlert, replaceSpaces, emailRegExp, shouldAutoFocus } from 'services/utils';
import { createBlurValidator, createNoSpacesChangeHandler } from 'services/formValidation';
import { ROUTE_PATHS } from 'routes/constants';
import emailRegistrationStore from 'stores/emailRegistrationStore';
import ComplexTranslate from 'components/ComplexTranslate';
import Alert from 'uikit/Alert/Alert';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import Tooltip from 'uikit/Tooltip/Tooltip';
import CustomCaptcha from 'uikit/CustomCaptcha';
import Loader from 'components/common/Loader';
import AuthPageLink from 'components/common/AuthPageLink';
import AuthMobileApp, { AUTH_MOBILE_APP_VARIANTS } from 'components/common/AuthMobileApp';
import { ReactComponent as AppleLogo } from 'assets/apple-logo.svg';
import { ReactComponent as GoogleLogo } from 'assets/google-logo.svg';

const logoPath = process.env.REACT_APP_LOGO_PATH;

// TRN-6922 Stage 2 — real design + logic, see docs/specs/TRN-6922_email_registration/spec.md §8.2. Structure mirrors
// components/Authorization/Login.js (mobile-only logo, Alert, Tooltip-wrapped disabled social placeholders,
// isLoading-disabled submit button) — captcha wiring copies components/Registration/User/Credentials.js:185-193.
const EmailStep = () => {
  const i18n = useContext(i18nContext);
  const captchaProvider = process.env.REACT_APP_CAPTCHA_PROVIDER;
  const captchaSiteKey =
    captchaProvider === 'GOOGLE'
      ? process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY?.trim()
      : captchaProvider === 'TURNSTILE'
        ? process.env.REACT_APP_TURNSTILE_CAPTCHA_SITE_KEY?.trim()
        : captchaProvider === 'HCAPTCHA'
          ? process.env.REACT_APP_HCAPTCHA_CAPTCHA_SITE_KEY?.trim()
          : null;
  const isCaptchaEnabled = !!(captchaProvider && captchaSiteKey);
  const recaptchaRef = createRef();

  const { error: storeError, fieldErrors, isLoading } = emailRegistrationStore;

  const form = useFormik({
    validateOnChange: false,
    // TRN-7489: the address the store still holds — after "Change email" on the Confirm step, or on Back/Forward
    // to this page (TRN-7473 keeps the store then); empty after the page-entry reset and restartAfterFailure().
    // The field shows it focused (autoFocus below).
    initialValues: { email: emailRegistrationStore.email, captcha: '' },
    validationSchema: Yup.object({
      email: Yup.string()
        .nullable(true)
        .required(i18n.getMessage('login.error.emptyEmail'))
        .max(50, i18n.getMessage('error.emailSize'))
        .trim()
        .matches(emailRegExp, { message: i18n.getMessage('login.error.invalidEmail') })
        .email(i18n.getMessage('login.error.invalidEmail')),
      ...(isCaptchaEnabled
        ? { captcha: Yup.string().nullable(true).required(i18n.getMessage('register.error.emptyCaptcha')) }
        : {})
    }),
    onSubmit: async (values) => {
      // Belt-and-suspenders per CLAUDE.md, same reasoning as Login.js's onSubmit.
      if (isLoading) return;

      emailRegistrationStore.setEmail(replaceSpaces(values.email));
      await emailRegistrationStore.begin(values.captcha);

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  });

  const { values, errors, handleSubmit, submitCount, setFieldValue } = form;

  const validateOnBlur = createBlurValidator(form);
  const handleEmailChange = createNoSpacesChangeHandler(form);

  return (
    <section className={'registration-form'}>
      <a
        href={process.env.REACT_APP_LINK_HOME_PAGE}
        target={'_blank'}
        rel={'noreferrer'}
        className={'registration-form__mobile-logo'}
      >
        <img src={logoPath} alt={'Transferra'} />
      </a>

      <div className={'registration-form__body'}>
        <div className={'registration-form__heading'}>
          <h1 className={'registration-form__title'}>{i18n.getMessage('registration.email.title')}</h1>
          <p className={'registration-form__subtitle'}>{i18n.getMessage('registration.email.subtitle')}</p>
        </div>

        <Alert type={'warning'} message={storeError && getErrorMessageForAlert(i18n, storeError)} />

        <div className={'registration-form__social'}>
          {/* UI-only per spec §8.2/TRN-6921 §7.5 precedent — no PKCE/provider SDK wiring in this ticket. */}
          <Tooltip content={i18n.getMessage('login.tooltip.socialComingSoon', { provider: 'Apple' })} direction={'top'}>
            <button type={'button'} className={'registration-form__social-btn'} disabled>
              <AppleLogo />
              {i18n.getMessage('registration.social.continueWithApple')}
            </button>
          </Tooltip>
          <Tooltip
            content={i18n.getMessage('login.tooltip.socialComingSoon', { provider: 'Google' })}
            direction={'top'}
          >
            <button type={'button'} className={'registration-form__social-btn'} disabled>
              <GoogleLogo />
              {i18n.getMessage('registration.social.continueWithGoogle')}
            </button>
          </Tooltip>
        </div>

        <div className={'registration-form__divider'}>
          <span />
          <p>{i18n.getMessage('login.divider.or')}</p>
          <span />
        </div>

        {/* autoComplete="off" is advisory only — the per-field autoComplete="new-password" below is what
            actually stops Chrome from offering a saved credential on this field, same trick as Login.js. */}
        <form
          className={'registration-form__form'}
          id={'registration-email'}
          onSubmit={handleSubmit}
          autoComplete={'off'}
        >
          <Input
            type={'email'}
            label={i18n.getMessage('login.label.email')}
            placeholder={i18n.getMessage('login.placeholder.email')}
            name={'email'}
            value={values.email}
            onChange={handleEmailChange}
            onBlur={validateOnBlur('email')}
            initialStatus={submitCount}
            isRequired={true}
            autoComplete={'new-password'}
            // No autofocus on touch devices (it opens the keyboard over the page) — except when the field comes back
            // pre-filled from "Change email" (TRN-7489), where the user has just asked to edit it.
            autoFocus={shouldAutoFocus() || !!emailRegistrationStore.email}
            isDisabled={isLoading}
            error={errors?.email || fieldErrors?.email}
            isApiError={!!storeError}
          />

          {isCaptchaEnabled && (
            <CustomCaptcha
              captchaProvider={captchaProvider}
              siteKey={captchaSiteKey}
              error={errors?.captcha}
              onChange={(token) => setFieldValue('captcha', token)}
              recaptchaRef={recaptchaRef}
            />
          )}

          {/* Per CLAUDE.md (disable in-flight-request buttons): isDisabled is driven by isLoading, not just
              form validity. Also disabled while the email is empty (or only spaces), per Figma — same
              pattern as PasswordStep.js's button; enabled from the first character typed. */}
          <Button
            className={'registration-form__continue-button'}
            type={'primary'}
            roleType={'submit'}
            size={'large'}
            fullWidth={true}
            onClick={() => {}}
            isDisabled={isLoading || !replaceSpaces(values.email)}
          >
            {isLoading ? <Loader /> : i18n.getMessage('registration.email.button')}
          </Button>
        </form>

        <div className={'registration-form__terms'}>
          <ComplexTranslate
            text={'register.text.creatingAccount'}
            interpolation={{
              user: (
                <a
                  href={process.env.REACT_APP_LINK_TERMS_AND_CONDITIONS}
                  target={'_blank'}
                  className={'registration-form__terms-link'}
                  rel={'noreferrer'}
                >
                  {i18n.getMessage('register.text.userAgreement')}
                </a>
              ),
              privacy: (
                <a
                  href={process.env.REACT_APP_LINK_PRIVACY_POLICY}
                  target={'_blank'}
                  className={'registration-form__terms-link'}
                  rel={'noreferrer'}
                >
                  {i18n.getMessage('register.text.userPrivacy')}
                </a>
              )
            }}
          />
        </div>

        <p className={'registration-form__signup'}>
          {i18n.getMessage('register.text.haveAccount')}{' '}
          <AuthPageLink to={ROUTE_PATHS.LOGIN} className={'registration-form__signup-link'} isDisabled={isLoading}>
            {i18n.getMessage('login.returnToLogIn')}
          </AuthPageLink>
        </p>
      </div>

      {/* TRN-7538: QR codes in the brand panel on desktop; these badges only while that panel is hidden. */}
      <AuthMobileApp variant={AUTH_MOBILE_APP_VARIANTS.BADGES} />
    </section>
  );
};

export default observer(EmailStep);
